// The task forces and what each was formed to do (kanban 838512f8).
//
// AI/Code check-in 2026-10-12. PJ: "Ravi and I determine we're going to
// create Alpha team. Alpha team is going to focus on these four, driving
// results in these four ways. And then everything that's talked about in
// that meeting that's relevant should be categorizable in each of those
// things." Ravi, on the Delta view: "this is Paul's pet list ... not
// everything that they work on."
//
// One card per task force: its DEFINITION (kanban 16a22d06 — the one copy
// the team view, the Quality door and Merlin's kickoff read; Ravi's emailed
// wording with PJ's amendments goes in here), its CHARTER verbatim (kanban
// 87ab737b — Ravi and PJ's "Team Charters: Alpha, Delta and Sigma", the
// whole text Merlin is handed every run and judges ownership against,
// folded behind "Read the charter"), its mandate areas in order
// with how much active work sits under each, what is not filed yet, and
// where the version came from. Admins edit in place (definition; add,
// remove, reorder, rename areas). Every roadmap run is handed this registry;
// the audit asks about unfiled work.

import { useState } from 'react';
import type { TeamMandate, TeamMandateListResponse, TeamMandateRequest } from '../../services/roadmapReports';
import {
  addArea,
  cleanTeamName,
  dirty,
  draftFrom,
  MAX_AREAS,
  MAX_CHARTER,
  MAX_DEFINITION,
  moveArea,
  removeArea,
  toRequest,
  updateArea,
  type MandateDraft,
} from './mandatesPanelUtils';
import styles from './RoadmapV2.module.css';

export type SaveMandateFn = (team: string, req: TeamMandateRequest) => Promise<void>;

interface Props {
  data: TeamMandateListResponse | null;
  /** Admin: save a team's areas; omitted → read-only. */
  onSave?: SaveMandateFn;
}

function errorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (typeof res?.data?.detail === 'string') return res.data.detail;
  if (res?.status === 403) return 'Only an admin can change a mandate.';
  return 'Could not save the mandate — try again.';
}

function MandateEditor({ team, mandate, onSave, onDone }: { team: string; mandate: TeamMandate | null; onSave: SaveMandateFn; onDone: () => void }) {
  const [draft, setDraft] = useState<MandateDraft>(() => draftFrom(mandate));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(team, toRequest(draft));
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={styles.mandateEditor} role="group" aria-label={`Edit the mandate of ${team}`} data-testid={`mandate-editor-${team}`}>
      <textarea
        className={`${styles.teamsInput} ${styles.mandateEditorDefinition}`}
        aria-label="Team definition"
        placeholder="What this team is, in the group's words (Ravi's definition, PJ's amendments)…"
        value={draft.definition}
        onChange={(e) => setDraft({ ...draft, definition: e.target.value })}
        maxLength={MAX_DEFINITION}
        rows={4}
        disabled={busy}
      />
      <textarea
        className={`${styles.teamsInput} ${styles.mandateEditorCharter}`}
        aria-label="Team charter"
        placeholder="The team's charter, pasted verbatim (purpose, scope, goals, members, cadence — as Ravi and PJ wrote it). Merlin reads it whole every run."
        value={draft.charter}
        onChange={(e) => setDraft({ ...draft, charter: e.target.value })}
        maxLength={MAX_CHARTER}
        rows={10}
        disabled={busy}
      />
      <ol className={styles.mandateEditorList}>
        {draft.areas.map((a, n) => (
          <li key={n} className={styles.mandateEditorRow}>
            <input
              className={styles.teamsInput}
              aria-label={`Area ${n + 1} title`}
              placeholder="Area, in the team's words…"
              value={a.title}
              onChange={(e) => setDraft(updateArea(draft, n, { title: e.target.value }))}
              maxLength={120}
              disabled={busy}
            />
            <input
              className={`${styles.teamsInput} ${styles.mandateEditorDesc}`}
              aria-label={`Area ${n + 1} description`}
              placeholder="What it covers (one sentence)"
              value={a.description}
              onChange={(e) => setDraft(updateArea(draft, n, { description: e.target.value }))}
              maxLength={400}
              disabled={busy}
            />
            <button type="button" className={styles.respondGhost} onClick={() => setDraft(moveArea(draft, n, -1))} disabled={busy || n === 0} aria-label={`Move area ${n + 1} up`}>↑</button>
            <button type="button" className={styles.respondGhost} onClick={() => setDraft(moveArea(draft, n, 1))} disabled={busy || n === draft.areas.length - 1} aria-label={`Move area ${n + 1} down`}>↓</button>
            <button type="button" className={styles.respondGhost} onClick={() => setDraft(removeArea(draft, n))} disabled={busy} aria-label={`Remove area ${n + 1}`}>×</button>
          </li>
        ))}
      </ol>
      <div className={styles.respondActions}>
        <button type="button" className={styles.respondGhost} onClick={() => setDraft(addArea(draft))} disabled={busy || draft.areas.length >= MAX_AREAS}>
          Add an area
        </button>
        <input
          className={`${styles.teamsInput} ${styles.mandateEditorBasis}`}
          aria-label="Where this list comes from"
          placeholder="Where this list comes from (a meeting, a deck)…"
          value={draft.basis}
          onChange={(e) => setDraft({ ...draft, basis: e.target.value })}
          maxLength={500}
          disabled={busy}
        />
        <button type="button" className={styles.respondPrimary} onClick={() => void save()} disabled={busy || !dirty(draft, mandate)}>
          {busy ? 'Saving…' : 'Save mandate'}
        </button>
        <button type="button" className={styles.respondGhost} onClick={onDone} disabled={busy}>
          Cancel
        </button>
      </div>
      {error && <div className={styles.respondError} role="alert">{error}</div>}
    </div>
  );
}

export function MandatesPanel({ data, onSave }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [newTeam, setNewTeam] = useState('');
  const [adding, setAdding] = useState(false);
  if (!data) return null;
  const canEdit = Boolean(onSave);
  const startNew = () => {
    const name = cleanTeamName(newTeam);
    if (!name) return;
    setAdding(true);
    setEditing(name);
  };
  return (
    <section className={`${styles.panel} ${styles.mandatesPanel}`} aria-label="Task forces and their mandates" data-testid="mandates-panel">
      <div className={styles.panelHead}>
        <h2>What each task force is for</h2>
        <span className={styles.meta}>
          Merlin files every initiative under one area of its owning team&apos;s mandate; work that fits none shows as &quot;outside its mandate&quot;, unfiled work is asked about on the next run.
        </span>
      </div>
      <div className={styles.mandateCards}>
        {data.teams.map((m) => (
          <article key={m.team} className={`${styles.mandateCard} ${editing === m.team ? styles.mandateCardEditing : ''}`} data-testid={`mandate-${m.team}`}>
            <header className={styles.mandateCardHead}>
              <h3>{m.team}</h3>
              <span className={styles.meta}>
                {m.areas.length === 0 ? 'no mandate written yet' : `${m.areas.length} area${m.areas.length === 1 ? '' : 's'}`}
                {m.areas.length > 0 && m.unfiled > 0 && <> · <span data-testid={`mandate-unfiled-${m.team}`}>{m.unfiled} not filed yet</span></>}
                {m.outside > 0 && <> · {m.outside} outside its mandate</>}
              </span>
              {canEdit && editing !== m.team && (
                <button type="button" className={styles.respondGhost} onClick={() => { setAdding(false); setEditing(m.team); }}>
                  {m.areas.length === 0 ? 'Write the mandate' : 'Edit'}
                </button>
              )}
            </header>
            {editing === m.team && onSave ? (
              <MandateEditor team={m.team} mandate={m} onSave={onSave} onDone={() => setEditing(null)} />
            ) : (
              <>
                {m.definition ? (
                  <p className={styles.mandateDefinition} data-testid={`mandate-definition-${m.team}`}>{m.definition}</p>
                ) : (
                  <p className={styles.muted} data-testid={`mandate-definition-${m.team}`}>No definition written yet.</p>
                )}
                {m.charter ? (
                  <details className={styles.mandateCharter} data-testid={`mandate-charter-${m.team}`}>
                    <summary>Read the charter</summary>
                    <pre className={styles.mandateCharterText}>{m.charter}</pre>
                  </details>
                ) : (
                  <p className={styles.muted} data-testid={`mandate-charter-${m.team}`}>No charter on record — Merlin files this team's work by the areas alone.</p>
                )}
                {m.areas.length === 0 ? (
                  m.definition ? null : <p className={styles.muted}>{m.basis ?? 'Nothing recorded yet.'}</p>
                ) : (
              <ol className={styles.mandateAreas}>
                {m.areas.map((a) => (
                  <li key={a.key} className={styles.mandateArea}>
                    <span className={styles.mandateAreaTitle}>{a.title}</span>
                    {a.description && <span className={styles.mandateAreaDesc}>{a.description}</span>}
                    <span className={styles.mandateAreaCount} title="Active initiatives filed under this area">
                      {a.initiatives}
                    </span>
                  </li>
                ))}
              </ol>
                )}
              </>
            )}
            {editing !== m.team && (
              <div className={styles.teamsPin}>
                {m.basis && (m.areas.length > 0 || m.definition) && <>{m.basis} · </>}
                set by {m.set_by_email} on {m.set_at.slice(0, 10)}
                {m.revisions > 0 && <> · {m.revisions} earlier version{m.revisions === 1 ? '' : 's'}</>}
              </div>
            )}
          </article>
        ))}
        {canEdit && onSave && (
          <article className={`${styles.mandateCard} ${styles.mandateCardNew} ${adding && editing ? styles.mandateCardEditing : ''}`} data-testid="mandate-new">
            {adding && editing ? (
              <>
                <header className={styles.mandateCardHead}><h3>{editing}</h3></header>
                <MandateEditor team={editing} mandate={null} onSave={onSave} onDone={() => { setEditing(null); setAdding(false); setNewTeam(''); }} />
              </>
            ) : (
              <div className={styles.teamsRow}>
                <input
                  className={styles.teamsInput}
                  aria-label="New task force"
                  placeholder="Add a task force…"
                  value={newTeam}
                  onChange={(e) => setNewTeam(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); startNew(); } }}
                  maxLength={40}
                />
                <button type="button" className={styles.respondGhost} onClick={startNew} disabled={!cleanTeamName(newTeam)}>
                  Add
                </button>
              </div>
            )}
          </article>
        )}
      </div>
    </section>
  );
}
